import { z } from '../renderer/src/schema-library';
import { PlaceTitleSchema, type PlaceTitle } from '../renderer/src/place-title';

export const GEOCODING_TYPES = ['locality', 'place', 'district', 'region', 'country'] as const;
export const GeocodingLookupSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  language: z.string().trim().max(100)
    .regex(/^[a-z]{2,3}(?:-[a-z0-9]{2,8})*(?:,[a-z]{2,3}(?:-[a-z0-9]{2,8})*)*$/i)
    .transform(value => value.toLowerCase()).default('en'),
  types: z.array(z.enum(GEOCODING_TYPES)).min(1).max(5).default(() => [...GEOCODING_TYPES])
    .transform(value => [...new Set(value)].sort()),
  worldview: z.enum(['ar', 'cn', 'in', 'jp', 'ma', 'rs', 'ru', 'tr', 'us']).default('us'),
}).strict();
export type GeocodingLookup = z.infer<typeof GeocodingLookupSchema>;

/** Canonical input retains exact coordinates and every provider option that affects the result. */
export function geocodingQuery(value: GeocodingLookup) {
  return { provider: 'mapbox', version: 'v6', operation: 'reverse', permanent: true,
    ...GeocodingLookupSchema.parse(value) } as const;
}

const NamedArea = z.object({ name: z.string() });
const Feature = z.object({ properties: z.object({
  feature_type: z.string(), name: z.string(),
  context: z.object({ locality: NamedArea.optional(), place: NamedArea.optional(),
    district: NamedArea.optional(), region: NamedArea.optional(), country: NamedArea.optional() }).optional(),
}) });

/** Preserve provider fields for subsequent internal uses, including legitimate empty results. */
export const GeocodingResponseSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(z.object({ type: z.literal('Feature'),
    properties: z.object({ feature_type: z.string().min(1), name: z.string().min(1) }).passthrough(),
  }).passthrough()).max(50),
}).passthrough();
export type GeocodingResponse = z.infer<typeof GeocodingResponseSchema>;
export const StartingPlaceResponseSchema = z.object({ place: PlaceTitleSchema.nullable() });

const label = (name: string | undefined) => name?.trim() && name.trim().length <= 120 ? name.trim() : undefined;

/** Prefers a settlement or official locality, then a broader geopolitical area. */
export function startingPlaceTitle(candidate: unknown): PlaceTitle | undefined {
  const response = z.object({ features: z.array(z.unknown()) }).safeParse(candidate);
  if (!response.success) return undefined;
  const areas = response.data.features.flatMap(value => {
    const feature = Feature.safeParse(value);
    return feature.success ? [feature.data.properties] : [];
  });
  const nameAt = (level: typeof GEOCODING_TYPES[number]) => {
    for (const area of areas) {
      const name = label(area.feature_type === level ? area.name : area.context?.[level]?.name);
      if (name) return name;
    }
    return undefined;
  };
  for (const level of GEOCODING_TYPES) {
    const name = nameAt(level);
    if (!name) continue;
    const candidates = level === 'country' ? [] : level === 'region'
      ? [nameAt('country')] : [nameAt('region'), nameAt('country')];
    const region = candidates.find(value => value && value.toLocaleLowerCase() !== name.toLocaleLowerCase()) ?? '';
    return PlaceTitleSchema.parse({ name, region });
  }
  return undefined;
}
